"use client";

import { getSiteContent, getWatchdogPricingFeatureRows } from "@/content/site";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useSessionContext } from "@/context/SessionContext";
import { CheckIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/Button";

const PriceList = () => {
  const { setSelectedProduct } = useSessionContext();
  const { setDoLogin } = useAppContext();
  const { isDark } = useAppContext();
  const { language } = useLanguage();
  const { pricingPlans } = getSiteContent(language);
  const copy = {
    cs: {
      featured: "Nejoblíbenější",
      scarcity: "Akční cena",
      choose: "Vybrat členství",
      currency: "Kč",
      watchdogService: "Služba Hlídací pes",
      portfolioService: "Služba Portfolio",
    },
    en: {
      featured: "Most popular",
      scarcity: "Special price",
      choose: "Choose membership",
      currency: "CZK",
      watchdogService: "Watchdog service",
      portfolioService: "Portfolio service",
    },
  }[language];

  return (
    <div className="pricelist-grid">
      {pricingPlans.map((product) => (
        <div
          key={product.value}
          className={`price-card ${product.featured ? "price-card--featured" : ""}`}
        >
          {product.featured && (
            <div className="price-card-badge">{copy.featured}</div>
          )}

          {/* Paw icons */}
          <div className="price-paws">
            {Array.from({ length: product.watchdogCount }).map((_, i) => (
              <div key={i} className="price-paw">
                <Image
                  src={
                    isDark
                      ? "/image/paw/paw-light.svg"
                      : "/image/paw/paw-dark.svg"
                  }
                  alt={`${copy.watchdogService} – ${product.name}`}
                  aria-hidden="true"
                  fill
                  className="object-contain"
                />
              </div>
            ))}
          </div>

          {/* Header */}
          <div className="price-card-header">
            <h3 className="price-name">{product.name}</h3>
            <p className="price-desc">{product.description}</p>
          </div>

          {/* Price */}
          <div className="price-amount-block">
            {product.priceOriginal > 0 && (
              <span className="price-original">
                {product.priceOriginal.toLocaleString(
                  language === "cs" ? "cs-CZ" : "en-US",
                )}{" "}
                {copy.currency}
              </span>
            )}
            <div className="price-row">
              <span
                className={`price-amount !font-semibold dark:!text-softiq-secondary ${
                  product.featured
                    ? "!text-softiq-secondary-shade-200"
                    : "!text-softiq-secondary-shade-700"
                }`}
              >
                {product.price}
              </span>
              <span className="price-period">{product.priceSub}</span>
            </div>
            {product.priceOriginal > 0 && (
              <p className="price-scarcity">{copy.scarcity}</p>
            )}
          </div>

          {/* Features */}
          <div className="flex flex-1 flex-col">
            <p
              className={`mb-3 text-[10px] font-bold uppercase tracking-[0.16em] dark:text-softiq-secondary ${
                product.featured
                  ? "text-softiq-secondary-shade-200"
                  : "text-softiq-secondary-shade-700"
              }`}
            >
              {copy.watchdogService}
            </p>
            <ul className="price-features !flex-none">
              {getWatchdogPricingFeatureRows(product, language).map(
                (feature) => (
                  <li
                    key={feature.label}
                    aria-hidden={!feature.included}
                    className={`price-feature min-h-6 ${
                      feature.included ? "" : "invisible"
                    }`}
                  >
                    <CheckIcon
                      className={`price-check dark:!text-softiq-secondary ${
                        product.featured
                          ? "!text-softiq-secondary-shade-200"
                          : "!text-softiq-secondary-shade-700"
                      }`}
                    />
                    <span>{feature.label}</span>
                  </li>
                ),
              )}
            </ul>

            <div className="mt-6 border-t border-border/60 pt-5">
              <p
                className={`mb-3 text-[10px] font-bold uppercase tracking-[0.16em] dark:text-softiq-secondary ${
                  product.featured
                    ? "text-softiq-secondary-shade-200"
                    : "text-softiq-secondary-shade-700"
                }`}
              >
                {copy.portfolioService}
              </p>
              <ul className="price-features">
                {product.portfolioFeatures.map((item) => (
                  <li key={item} className="price-feature">
                    <CheckIcon
                      className={`price-check dark:!text-softiq-secondary ${
                        product.featured
                          ? "!text-softiq-secondary-shade-200"
                          : "!text-softiq-secondary-shade-700"
                      }`}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTA */}
          <Button asChild variant={product.featured ? "default" : "outline"}>
            <Link
              href="/#prihlaseni"
              onClick={() => {
                setDoLogin(false);
                setSelectedProduct?.(product.value);
              }}
            >
              {copy.choose}
            </Link>
          </Button>
        </div>
      ))}
    </div>
  );
};

export default PriceList;
